import {useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectDetails} from '~/CreateFabricInvitation/reducers/details/selectors';
import {scrollEventTitleModuleIntoView} from '~/CreateFabricInvitation/utils/misc';
import type {Address} from '~/common/types/location';
import {AddressOverlay} from '~/OneStepCreateInvitation/routes/OneStepCreatePage/components/DetailsFields/components/WhereFields/AddressOverlay';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {MapIcon} from '~library/svg/v2/MapIcon';
import {neutral} from '~libSass/v2/_exports.sass';

export const Location = () => {
  const [showAddressOverlay, setShowAddressOverlay] = useState(false);
  const {address, location, meetingUrl}: {address: Address; location: string; meetingUrl: string} =
    useSelector(selectDetails);

  const hasLocation = Object.values(address).some(Boolean) || !!location;
  const hasAdditionalLocationDetails = Boolean(
    address.street || address.city || address.state || address.zip || address.unitNum
  );

  const openAddressOverlay = useCallback(() => {
    setShowAddressOverlay(true);
    if (hasLocation) scrollEventTitleModuleIntoView();
  }, [hasLocation]);

  const closeAddressOverlay = useCallback(() => {
    setShowAddressOverlay(false);
    scrollEventTitleModuleIntoView();
  }, []);

  const boxText = useMemo(() => {
    if (meetingUrl) return meetingUrl;
    if (hasLocation)
      return (
        <Typography variant="paragraph2" as="span" semibold={hasAdditionalLocationDetails}>
          {location}
        </Typography>
      );
    return 'Add a location';
  }, [hasAdditionalLocationDetails, hasLocation, location, meetingUrl]);

  return (
    <div className="create-flow__form-section event-details__location">
      <Typography variant="label2" size="small" className="input-label">
        Location
      </Typography>
      <Button
        className="event-details__location-button"
        variant="unstyled-button"
        id="location"
        data-qa-id="event-location-trigger"
        type="button"
        onClick={openAddressOverlay}
        icon={meetingUrl ? '' : <MapIcon ratio={0.5} color={neutral} />}
      >
        <Typography
          className="event-details__location-value"
          variant="paragraph2"
          color={hasLocation || meetingUrl ? 'charcoal' : 'placeholder'}
        >
          {boxText}

          {hasAdditionalLocationDetails ? (
            <Typography
              variant="paragraph2"
              as="span"
              className="event-details__location-value--additional"
              color={location ? 'asphalt' : 'charcoal'}
            >
              {address.street ? `${address.street}, ` : null}
              {address.unitNum ? `${address.unitNum}, ` : null}
              {address.city ? `${address.city}, ` : null}
              {address.state ? `${address.state} ` : null}
              {address.zip ? address.zip : null}
            </Typography>
          ) : null}
        </Typography>
      </Button>
      <AddressOverlay closeOverlay={closeAddressOverlay} isOpen={showAddressOverlay} />
    </div>
  );
};
